<template>
  <GenericCard
    :locked="locked"
    has-delete
    :subtitle="subtitle"
    :on-delete="onCardDelete"
    :on-action="submitForm"
    :on-refresh="onCardRefresh"
    :invalid="isSectionValid"
    :dirty="isSectionDirty"
    :create="create"
    :section="section"
    has-footer
    title="Email"
    has-share
    :card-identifier="cardIdentifier"
    :survey-validation="surveyValidation"
    :survey-identifier="surveyIdentifier"
    @created="onEmailCreated"
  >
    <v-form
      :disabled="locked"
      @submit.prevent="submitForm"
    >
      <EmailFields
        v-model="section"
        :is-dirty.sync="isSectionDirty"
        :is-valid.sync="isSectionValid"
      />
    </v-form>
    <template #header>
      <v-chip
        v-if="section.primary"
        color="success"
        outlined
        label
      >
        <v-icon
          small
          left
        >
          mdi-check
        </v-icon>
        Primary
      </v-chip>
      <v-btn
        v-else
        small
        @click="dialog = true"
      >
        Set Primary
      </v-btn>
    </template>
    <v-dialog
      v-model="dialog"
      persistent
      max-width="290"
    >
      <v-card>
        <v-card-title class="text-h5">
          Set Primary Email
        </v-card-title>
        <v-card-text>Would you like to set <b>{{ section.address }}</b> as primary email?</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            color="green darken-1"
            text
            @click="dialog = false"
          >
            No
          </v-btn>
          <v-btn
            color="green darken-1"
            text
            @click="onSetPrimaryEmail"
          >
            Yes
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </GenericCard>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'

import { alertHandler } from '@/alertHandler'

import { PARTY_NAMESPACE } from '@/store/party'

import EmailFields from './EmailFields.vue'
import GenericCard from '@/components/Party/PartyEdit/SectionCards/GenericCard.vue'

export default {
  name: 'EmailCard',

  components: {
    EmailFields,
    GenericCard,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },

    surveyIdentifier: {
      type: String,
      default: null,
    },

    surveyValidation: {
      type: Boolean,
      default: false,
    },
  },

  setup () {
    return {
      ...alertHandler(),
    }
  },

  data () {
    return {
      cached_address: null,
      dialog: false,
      isSectionDirty: false,
      isSectionValid: true,
      section: {},
    }
  },

  computed: {
    cardIdentifier () { return `email-card-${this.sectionIdentifier}` },
    create () { return this.item.id === null },

    locked: {
      get () {
        return this.peEmailCardLock(this.sectionIdentifier)
      },

      set (value) {
        this.setPeEmailCardLock({ index: this.sectionIdentifier, locked: value })
      },
    },

    sectionIdentifier () { return this.section.id || 'new' },

    subtitle () {
      if (this.create) return ''
      return `(${this.section.owner} <${this.section.address}>)`
    },

    ...mapGetters(PARTY_NAMESPACE, [
      'peEmailCardLock',
    ]),
  },

  watch: {
    item: {
      immediate: true,

      handler: function () {
        this.section = { ...this.item }
      },

      deep: true,
    },

    'section.no_email' (val) {
      if (val) {
        this.cached_address = this.section.address
        this.$nextTick().then(_ => { this.section.address = '' })
      } else if (this.cached_address) {
        this.section.address = this.cached_address
        this.$nextTick().then(_ => { this.cached_address = null })
      }
    },
  },

  created () {
    if (!this.create) this.initPeEmailCard(this.sectionIdentifier)
  },

  methods: {
    getErrorMessages (validator) { return validator?.$errors.map(e => e.$message) },

    async onCardCreate () {
      return this.peEmailCardCreate({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardDelete () {
      if (this.create) return this.peEmailCardCreateUndo()
      else return this.peEmailCardDelete({ id: this.section.id, index: this.sectionIdentifier })
    },

    async onCardRefresh () {
      return this.peEmailCardRefresh({ index: this.sectionIdentifier, section: this.section })
    },

    async onCardUpdate () {
      return this.peEmailCardUpdate({ id: this.section.id, index: this.sectionIdentifier, section: this.section })
    },

    onEmailCreated () {
      this.dialog = true
    },

    async onSectionUnlock () {
      return this.setPeEmailCardLock({ index: this.sectionIdentifier, locked: false })
    },

    onSetPrimaryEmail () {
      this.section.primary = true
      this.dialog = false
      this.onCardUpdate().then(_ => {
        this.peEmailCardRefreshAll()
      })
    },

    async submitForm () {
      if (this.locked) return this.onSectionUnlock()
      else if (this.create) return this.onCardCreate()
      else return this.onCardUpdate()
    },

    ...mapMutations(PARTY_NAMESPACE, [
      'peEmailCardCreateUndo',
      'setPeEmailCardLock',
    ]),

    ...mapActions(PARTY_NAMESPACE, [
      'initPeEmailCard',
      'peEmailCardCreate',
      'peEmailCardDelete',
      'peEmailCardRefresh',
      'peEmailCardRefreshAll',
      'peEmailCardUpdate',
    ]),
  },
}
</script>
